import React, { useCallback, useMemo } from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

interface InputProps {
  label?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  error?: boolean;
  helperText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  showClearButton?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

// Memoize static styles outside component to prevent recreation
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#D9D9D9",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    fontSize: "14px",
    color: "#A6A6A6",
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-hp-error)",
  },
  "& .MuiInputBase-root.Mui-error": {
    color: "var(--color-hp-error)",
  },
};

const HPInput: React.FC<InputProps> = React.memo(
  ({
    label,
    value,
    onChange,
    type = "text",
    className,
    placeholder,
    disabled = false,
    multiline = false,
    rows,
    error = false,
    helperText,
    startIcon,
    endIcon,
    showClearButton = false,
    inputProps,
  }) => {
    const handleClear = useCallback(() => {
      if (onChange) {
        const event = {
          target: { value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    }, [onChange]);

    // Memoize label style to prevent recreation
    const labelStyle = useMemo(
      () => ({
        color: error
          ? "var(--color-hp-error)"
          : "var(--color-hp-text-day-primary)",
      }),
      [error],
    );

    // Memoize slot props to prevent recreation
    const slotProps = useMemo(
      () => ({
        input: {
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : undefined,
          endAdornment:
            (showClearButton && value) || endIcon ? (
              <>
                {showClearButton && value && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClear} edge="end" size="small">
                      <HighlightOffIcon className="text-hp-disabled" />
                    </IconButton>
                  </InputAdornment>
                )}
                {endIcon && (
                  <InputAdornment position="end">{endIcon}</InputAdornment>
                )}
              </>
            ) : undefined,
        },
        htmlInput: inputProps,
      }),
      [startIcon, endIcon, showClearButton, value, handleClear, inputProps],
    );

    return (
      <div className={className}>
        {label && (
          <label className="block text-xs font-medium mb-1" style={labelStyle}>
            {label}
          </label>
        )}
        <TextField
          value={value}
          onChange={onChange}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          multiline={multiline}
          rows={rows}
          error={error}
          helperText={helperText}
          variant="outlined"
          size="small"
          fullWidth
          sx={textFieldSx}
          slotProps={slotProps}
        />
      </div>
    );
  },
);

HPInput.displayName = "HPInput";

export default HPInput;
